import { zodResolver } from '@hookform/resolvers/zod';
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation } from 'react-router';
import { z } from 'zod';
import { useAuth } from '../auth/AuthProvider';
import { authErrorMessage } from '../auth/errors';
import { Button, Field, Input } from '../components/ui';
import { auth } from '../lib/firebase';

const loginSchema = z.object({
  email: z.email({ error: 'الإيميل غير صحيح.' }),
  password: z.string().min(1, { error: 'كلمة السر مطلوبة.' }),
});
type LoginValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const state = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });

  if (state.status === 'admin') {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setError(null);
    setNotice(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e) {
      setError(authErrorMessage(e));
    }
  });

  const onForgot = async () => {
    const email = getValues('email');
    const parsed = loginSchema.shape.email.safeParse(email);
    if (!parsed.success) {
      setError('الإيميل مطلوب أولاً، وبعدها "نسيت كلمة السر".');
      return;
    }
    setError(null);
    try {
      await sendPasswordResetEmail(auth, parsed.data);
    } catch {
      // Same message either way, so the form doesn't reveal which emails exist.
    }
    setNotice('إذا الإيميل مسجّل عندنا، رح يوصله رابط لتغيير كلمة السر.');
  };

  const notAdmin = state.status === 'signedOut' && state.reason === 'notAdmin';

  return (
    <main className="grid min-h-dvh place-items-center px-4">
      <form onSubmit={onSubmit} noValidate className="w-full max-w-sm space-y-5 rounded-dar border border-sand bg-white p-6">
        <header className="space-y-1">
          <h1>
            <img src="/dar-lockup-light.svg" alt="DAR · دار" width={144} height={40} className="h-10 w-auto" />
          </h1>
          <p className="text-sm text-muted">لوحة التحكم</p>
        </header>

        {notAdmin ? (
          <p role="alert" className="rounded-dar bg-cream p-3 text-sm">
            هذا الحساب ما عنده صلاحية دخول للوحة التحكم.
          </p>
        ) : null}

        <Field label="الإيميل" htmlFor="email" error={errors.email?.message}>
          <Input id="email" type="email" autoComplete="username" dir="ltr" invalid={!!errors.email} {...register('email')} />
        </Field>

        <Field label="كلمة السر" htmlFor="password" error={errors.password?.message}>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            dir="ltr"
            invalid={!!errors.password}
            {...register('password')}
          />
        </Field>

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p role="status" className="text-sm text-muted">
            {notice}
          </p>
        ) : null}

        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? 'لحظة…' : 'تسجيل الدخول'}
        </Button>

        <Button type="button" variant="link" onClick={onForgot} className="text-sm">
          نسيت كلمة السر؟
        </Button>
      </form>
    </main>
  );
}
